import moment from 'moment-timezone'

import { Event, SessionSaleTarget, WebinarTarget } from './types'

export type WaitlistState = 'open' | 'waitlist' | 'sold_out' | 'unknown'

export const isWebinar = (event: Event): boolean => !!event && event.type === 'webinar'
export const isSessionSale = (event: Event): boolean => !!event && event.type === 'session_sale'

// The date the offering is "about" — what a card shows and what the index view
// orders by. Session sales advertise a run window; webinars just start.
export const primaryDate = (event: Event): string => {
  if (!event || !event.target) return null

  if (event.type === 'session_sale') {
    const target = event.target as SessionSaleTarget
    return target.advertised_start_date || target.open_registration_end_at || null
  }

  return (event.target as WebinarTarget).start_at || null
}

// Where the offering sits on the calendar grid. A program's run window would
// swallow whole months, so it gets a single marker on the deadline members
// actually have to act on.
export const calendarDate = (event: Event): string => {
  if (!event || !event.target) return null

  if (event.type === 'session_sale') {
    return (event.target as SessionSaleTarget).open_registration_end_at || null
  }

  return (event.target as WebinarTarget).start_at || null
}

// External events route off-site until there's an internal recording to
// show — checking video_url (rather than a start/end time window) means
// there's never a gap where "View Details" lands on a page with nothing on
// it yet, regardless of how long the recording takes to get posted.
export const linkTo = (event: Event): string => {
  if (!event || !event.target) return null

  if (isWebinar(event)) {
    const target = event.target as WebinarTarget
    if (target.is_external && target.external_url && !target.video_url) {
      return target.external_url
    }
  }

  const { slug } = event.target
  if (!slug) return null

  return event.type === 'session_sale' ? `/sessions/${slug}` : `/webinars/${slug}`
}

// For surfaces that hold a bare webinar rather than an `{ type, target }` event.
export const webinarLinkTo = (webinar: WebinarTarget): string =>
  linkTo({ type: 'webinar', target: webinar } as Event)

// linkTo() returns an absolute URL for external events and an app-relative
// path otherwise. Contexts outside the SPA (calendar invites, emails) need a
// real absolute URL either way — this adds the origin only when linkTo()
// didn't already return one, instead of blindly concatenating the two.
export const absoluteLinkTo = (event: Event): string => {
  const link = linkTo(event)
  if (!link) return link

  return /^https?:\/\//.test(link) ? link : `${window.location.origin}${link}`
}

// The API only filters on the `events` row's end_at, which for session sales is
// unrelated to when registration closes — so this filter is load-bearing, not a
// belt-and-braces duplicate of the server.
export const isRegistrationOpen = (event: Event, now = moment()): boolean => {
  if (!isSessionSale(event)) return true

  const closesAt = (event.target as SessionSaleTarget).open_registration_end_at
  return !!closesAt && moment(closesAt).isAfter(now)
}

// num_reservations_available and waitlist_price are only computed for fsp and
// generic sales; for alumni and writing_challenge they're null. A null count
// means we don't know, never that it's sold out.
export const waitlistState = (target: SessionSaleTarget): WaitlistState => {
  if (!target || target.num_reservations_available == null) return 'unknown'
  if (target.num_reservations_available > 0) return 'open'
  return target.waitlist_price != null ? 'waitlist' : 'sold_out'
}

export const isRegistered = (event: Event): boolean => {
  if (!event || !event.target) return false

  return event.type === 'session_sale'
    ? (event.target as SessionSaleTarget).registration_status === 'reserved'
    : !!(event.target as WebinarTarget).registered
}
